/**
 * Text side of an Outwrite task review panel.
 * Supports `task_type` resources (keys[] / given) and legacy flat chart tasks.
 *
 * Keep templates keyed by `keyFeatures[].type` so new feature kinds only
 * need one entry (or fall through to the generic formatter).
 */

const META_KEYS = new Set(['type', 'rank', '_comment']);

/**
 * @param {object} feature OutwriteKeyFeature
 * @returns {string}
 */
export function describeKeyFeature(feature) {
  if (!feature || typeof feature !== 'object') {
    return '';
  }
  const template = KEY_FEATURE_TEMPLATES[feature.type];
  if (template) {
    return template(feature);
  }
  return describeKeyFeatureGeneric(feature);
}

/**
 * Fallback for unknown / future feature types: `type` plus remaining fields.
 * @param {object} feature
 * @returns {string}
 */
export function describeKeyFeatureGeneric(feature) {
  const parts = [feature.type || 'feature'];
  for (const [key, value] of Object.entries(feature)) {
    if (META_KEYS.has(key) || value == null) continue;
    parts.push(`${key}=${formatFieldValue(value)}`);
  }
  return parts.join(' ');
}

const KEY_FEATURE_TEMPLATES = {
  shape: (f) => {
    let s = `${f.series}: ${f.shape} (${f.from} → ${f.to})`;
    if (f.endsWhereItStarted) s += ', ends where it started';
    return s;
  },
  crossover: (f) => {
    const a = f.series?.[0];
    const b = f.series?.[1];
    const where = f.betweenLabel || (f.at != null ? `at ${f.at}` : '');
    const overtaking = f.overtaking ? `, ${f.overtaking} ahead after` : '';
    let s = `crossover ${a} / ${b}${where ? ` ${where}` : ''}${overtaking}`;
    if (f.inProjection) s += ' — in the projected years';
    return s;
  },
  repeatedCrossings: (f) =>
    `${f.series?.[0]} and ${f.series?.[1]} cross ${f.count} times`,
  orderUnchanged: (f) =>
    `order never changes: ${f.highest} highest, ${f.lowest} lowest`,
  finishLevel: (f) =>
    `${f.series?.[0]} and ${f.series?.[1]} finish level at ${f.atLabel}`,
  peak: (f) => `${f.series} peaks at ${f.value} at ${f.atLabel}`,
  trough: (f) => `${f.series} bottoms at ${f.value} at ${f.atLabel}`,
  plateau: (f) => `${f.series} flattens from ${f.fromLabel}`,
  largestSingleChange: (f) =>
    `biggest single move: ${f.series} ${formatSigned(f.delta)} (${f.fromLabel}→${f.toLabel})`,
  converging: (f) => `converging: spread ${f.spread?.[0]} → ${f.spread?.[1]}`,
  diverging: (f) => `diverging: spread ${f.spread?.[0]} → ${f.spread?.[1]}`,
  largestFall: (f) => `${f.series}: largest fall ${formatSigned(f.delta)}`,
  largestRise: (f) => `${f.series}: largest rise ${formatSigned(f.delta)}`,
};

function formatSigned(n) {
  if (typeof n !== 'number' || Number.isNaN(n)) return String(n);
  return n > 0 ? `+${n}` : String(n);
}

function formatFieldValue(value) {
  if (Array.isArray(value)) {
    return value.map(formatFieldValue).join(', ');
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
}

function pushLinesSection(sections, id, title, lines) {
  if (!lines?.length) return;
  sections.push({ id, title, lines });
}

function modelAnswerLines(modelAnswer) {
  if (!modelAnswer || typeof modelAnswer !== 'object') return [];
  const lines = [];
  for (const band of ['band6', 'band7']) {
    const part = modelAnswer[band];
    if (part == null) {
      lines.push(`${band}: null`);
      continue;
    }
    lines.push(`${band}:`);
    for (const key of ['paraphrase', 'overview', 'body']) {
      const val = part[key];
      lines.push(`  ${key}: ${val == null ? 'null' : val}`);
    }
  }
  return lines;
}

function keyValue(task, id) {
  if (!Array.isArray(task.keys)) return undefined;
  const entry = task.keys.find((k) => k && k.id === id);
  return entry?.value;
}

function isTaskResource(task) {
  return typeof task?.task_type === 'string' && Array.isArray(task.keys);
}

/**
 * Build structured review sections for the task (preview right-hand panel).
 *
 * @param {object} task OutwriteTaskResource | OutwriteChartTask
 * @returns {{ id: string, title?: string, lines: string[] }[]}
 */
export function buildTaskReviewSections(task) {
  if (!task) return [];
  return isTaskResource(task)
    ? buildResourceReviewSections(task)
    : buildLegacyReviewSections(task);
}

function buildResourceReviewSections(task) {
  const sections = [];
  const given = task.given?.data || {};

  const metaLines = [];
  if (task.id != null) metaLines.push(`id: ${task.id}`);
  if (task.task_type != null) metaLines.push(`task_type: ${task.task_type}`);
  if (task.topic != null) metaLines.push(`topic: ${task.topic}`);
  const quantityKind = keyValue(task, 'quantity_kind') ?? given.quantityKind;
  if (quantityKind != null) metaLines.push(`quantityKind: ${quantityKind}`);
  const tense = keyValue(task, 'tense') ?? given.tense;
  if (tense != null) {
    let tenseLine = `tense: ${tense}`;
    if (tense === 'mixed' && given.projectedFrom != null) {
      tenseLine += `   projected from ${given.projectedFrom}`;
    }
    metaLines.push(tenseLine);
  }
  {
    const bits = [];
    if (task.difficulty != null) bits.push(`difficulty: ${task.difficulty}`);
    const units = given.axes?.axisY?.label;
    if (units != null) bits.push(`units: ${units}`);
    if (bits.length) metaLines.push(bits.join('   '));
  }
  if (given.axes?.axisX) {
    const axis = given.axes.axisX;
    metaLines.push(`axis: type=${axis.type}  label=${axis.label}`);
  }
  pushLinesSection(sections, 'meta', 'META', metaLines);

  const features = [...(keyValue(task, 'chart_features') || [])].sort(
    (a, b) => (a.rank ?? 0) - (b.rank ?? 0),
  );
  if (features.length) {
    sections.push({
      id: 'keyFeatures',
      title: 'KEY FEATURES (rank order)',
      lines: features.map((f) => {
        const rank = f.rank != null ? String(f.rank).padStart(2, ' ') : '  ';
        return `${rank}. ${describeKeyFeature(f)}`;
      }),
    });
  }

  const movement = keyValue(task, 'lexis_movement');
  const quantity = keyValue(task, 'lexis_quantity');
  const lexisLines = [];
  if (Array.isArray(movement)) {
    lexisLines.push(`movement: ${movement.join(', ')}`);
  }
  if (Array.isArray(quantity)) {
    lexisLines.push(`quantity: ${quantity.join(', ')}`);
  }
  pushLinesSection(sections, 'targetLexis', 'TARGET LEXIS', lexisLines);

  const groupings = keyValue(task, 'groupings');
  if (Array.isArray(groupings) && groupings.length) {
    pushLinesSection(
      sections,
      'groupings',
      'GROUPINGS',
      groupings.map((g) => {
        const buckets = (g.buckets || [])
          .map((b) => `[${(b || []).join(', ')}]`)
          .join(' | ');
        return `${g.name}: ${buckets}`;
      }),
    );
  }

  if (Array.isArray(task.commonErrors) && task.commonErrors.length) {
    pushLinesSection(sections, 'commonErrors', 'COMMON ERRORS', task.commonErrors);
  }

  const modelLines = modelAnswerLines(task.modelAnswer);
  pushLinesSection(sections, 'modelAnswer', 'MODEL ANSWER', modelLines);

  const seriesObj = given.series;
  if (seriesObj && typeof seriesObj === 'object' && !Array.isArray(seriesObj)) {
    const x = Array.isArray(given.x) ? given.x : [];
    pushLinesSection(
      sections,
      'series',
      'SERIES',
      Object.entries(seriesObj).map(([label, ys]) => {
        const pts = x
          .map((xv, i) => `(${xv}, ${Array.isArray(ys) ? ys[i] : '?'})`)
          .join(' ');
        return `${label}: ${pts}`;
      }),
    );
  }

  const prompt =
    task.to_finds?.[0]?.prompt || given.stem || null;
  if (prompt) {
    sections.push({
      id: 'prompt',
      title: 'PROMPT',
      lines: [prompt],
    });
  }

  return sections;
}

function buildLegacyReviewSections(task) {
  const sections = [];

  const metaLines = [];
  if (task.id != null) metaLines.push(`id: ${task.id}`);
  if (task.chartType != null) metaLines.push(`chartType: ${task.chartType}`);
  if (task.topic != null) metaLines.push(`topic: ${task.topic}`);
  if (task.quantityKind != null) {
    metaLines.push(`quantityKind: ${task.quantityKind}`);
  }
  if (task.tense != null) {
    let tenseLine = `tense: ${task.tense}`;
    if (task.tense === 'mixed' && task.projectedFrom != null) {
      tenseLine += `   projected from ${task.projectedFrom}`;
    }
    metaLines.push(tenseLine);
  }
  {
    const bits = [];
    if (task.difficulty != null) bits.push(`difficulty: ${task.difficulty}`);
    if (task.units != null) bits.push(`units: ${task.units}`);
    if (bits.length) metaLines.push(bits.join('   '));
  }
  if (task.timeframe?.from != null && task.timeframe?.to != null) {
    metaLines.push(`timeframe: ${task.timeframe.from}–${task.timeframe.to}`);
  }
  if (task.axis) {
    const axisBits = [`type=${task.axis.type}`, `label=${task.axis.label}`];
    if (task.axis.unit) axisBits.push(`unit=${task.axis.unit}`);
    if (Array.isArray(task.axis.categories)) {
      axisBits.push(`categories=${task.axis.categories.join(', ')}`);
    }
    metaLines.push(`axis: ${axisBits.join('  ')}`);
  }
  pushLinesSection(sections, 'meta', 'META', metaLines);

  const features = [...(task.keyFeatures || [])].sort(
    (a, b) => (a.rank ?? 0) - (b.rank ?? 0),
  );
  if (features.length) {
    sections.push({
      id: 'keyFeatures',
      title: 'KEY FEATURES (rank order)',
      lines: features.map((f) => {
        const rank = f.rank != null ? String(f.rank).padStart(2, ' ') : '  ';
        return `${rank}. ${describeKeyFeature(f)}`;
      }),
    });
  }

  if (task.targetLexis) {
    const lexisLines = [];
    if (Array.isArray(task.targetLexis.movement)) {
      lexisLines.push(`movement: ${task.targetLexis.movement.join(', ')}`);
    }
    if (Array.isArray(task.targetLexis.quantity)) {
      lexisLines.push(`quantity: ${task.targetLexis.quantity.join(', ')}`);
    }
    pushLinesSection(sections, 'targetLexis', 'TARGET LEXIS', lexisLines);
  }

  if (Array.isArray(task.groupings) && task.groupings.length) {
    pushLinesSection(
      sections,
      'groupings',
      'GROUPINGS',
      task.groupings.map((g) => {
        const buckets = (g.buckets || [])
          .map((b) => `[${(b || []).join(', ')}]`)
          .join(' | ');
        return `${g.name}: ${buckets}`;
      }),
    );
  }

  if (Array.isArray(task.commonErrors) && task.commonErrors.length) {
    pushLinesSection(sections, 'commonErrors', 'COMMON ERRORS', task.commonErrors);
  }

  const modelLines = modelAnswerLines(task.modelAnswer);
  pushLinesSection(sections, 'modelAnswer', 'MODEL ANSWER', modelLines);

  if (Array.isArray(task.series) && task.series.length) {
    pushLinesSection(
      sections,
      'series',
      'SERIES',
      task.series.map((s) => {
        const pts = (s.points || [])
          .map((p) => `(${p[0]}, ${p[1]})`)
          .join(' ');
        return `${s.label}: ${pts}`;
      }),
    );
  }

  if (task.prompt) {
    sections.push({
      id: 'prompt',
      title: 'PROMPT',
      lines: [task.prompt],
    });
  }

  return sections;
}
